import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Plus, X } from 'lucide-react';
import '../../component-styles/SalesAccountSettings.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import {
  BUILT_IN_FREIGHT_DESTINATIONS,
  WILL_CALL_DESTINATION,
  getDestinationAddress,
  getFreightDestinationOptions,
  hasDestinationAddress,
  slugifyFreightDestination,
} from '../../utils/freightDestinations.js';
import AddressAutocomplete from '../common/AddressAutocomplete';
import {
  BUILT_IN_PO_STATUSES,
  BUILT_IN_SCHEDULE_STATUSES,
  normalizeCustomStatuses,
} from '../../utils/orderStatuses.js';

const DEFAULT_ROWS = [
  {
    label: 'Time Billing',
    code: 'TB',
    costSales: 'C - Labor Cost',
    incomeSales: 'R - Design Income',
    incomeMarkup: 'R - Design Income',
  },
  {
    label: 'Upholstery',
    code: 'UPH',
    costSales: 'C - Labor Cost',
    incomeSales: 'R - Labor Income',
    incomeMarkup: 'R - Labor Income',
  },
  {
    label: 'Fabrication',
    code: 'FAB',
    costSales: 'C - Labor Cost',
    incomeSales: 'R - Labor Income',
    incomeMarkup: 'R - Labor Income',
  },
  {
    label: 'Installation',
    code: 'INST',
    costSales: 'C - Labor Cost',
    incomeSales: 'R - Labor Income',
    incomeMarkup: 'R - Labor Income',
  },
  {
    label: 'Reimbursable Expense',
    code: 'REIMB',
    costSales: 'R - Reimbursable Expense',
    incomeSales: 'R - Reimbursable Income',
    incomeMarkup: 'R - Reimbursable Income',
  },
  {
    label: 'Accessories',
    code: 'ACC',
    costSales: 'C - Merchandise Cost',
    incomeSales: 'R - Merchandise Income',
    incomeMarkup: 'R - Merchandise Income',
  },
  {
    label: 'Furniture',
    code: 'FURN',
    costSales: 'C - Merchandise Cost',
    incomeSales: 'R - Merchandise Income',
    incomeMarkup: 'R - Merchandise Income',
  },
  {
    label: 'Shipping',
    code: 'SHIP',
    costSales: 'C - Shipping & Delivery',
    incomeSales: 'R - Shipping & Delivery',
    incomeMarkup: 'R - Shipping & Delivery',
  },
  {
    label: 'Freight',
    code: 'FRT',
    costSales: 'C - Shipping & Delivery',
    incomeSales: 'R - Shipping & Delivery',
    incomeMarkup: 'R - Shipping & Delivery',
  },
  {
    label: 'Tariffs',
    code: 'TRF',
    costSales: 'C - Tariff & Duties',
    incomeSales: 'R - Tariff & Duties',
    incomeMarkup: 'R - Tariff & Duties',
  },
  {
    label: 'Labor',
    code: 'LAB',
    costSales: 'C - Labor Cost',
    incomeSales: 'R - Labor Income',
    incomeMarkup: 'R - Labor Income',
  },
  {
    label: 'Window Treatments',
    code: 'WIN',
    costSales: 'C - Merchandise Cost',
    incomeSales: 'R - Merchandise Income',
    incomeMarkup: 'R - Merchandise Income',
  },
  {
    label: 'Wall Coverings',
    code: 'WALL',
    costSales: 'C - Merchandise Cost',
    incomeSales: 'R - Merchandise Income',
    incomeMarkup: 'R - Merchandise Income',
  },
  {
    label: 'Reimbursables',
    code: 'REIMB',
    costSales: 'R - Reimbursable Expense',
    incomeSales: 'R - Reimbursable Income',
    incomeMarkup: 'R - Reimbursable Income',
  },
];

export default function SalesAccountSettings() {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('idToken');

  const [salesAccounts, setSalesAccounts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [qbAccounts, setQbAccounts] = useState([]);
  const [qbConnected, setQbConnected] = useState(false);
  const [mappingTab, setMappingTab] = useState('products');
  const [mappingSaving, setMappingSaving] = useState(false);
  const [showLegacySection, setShowLegacySection] = useState(false);
  const [defaultShippingDestination, setDefaultShippingDestination] = useState('designer_warehouse');
  const [shippingSaving, setShippingSaving] = useState(false);
  const [freightDestinations, setFreightDestinations] = useState([]);
  const [newDestinationLabel, setNewDestinationLabel] = useState('');
  const [destinationSaving, setDestinationSaving] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [customOrderStatuses, setCustomOrderStatuses] = useState([]);
  const [newStatusLabel, setNewStatusLabel] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [mapping, setMapping] = useState({
    products: {
      defaultIncomeAccountId: '',
      defaultIncomeAccountName: '',
      defaultExpenseAccountId: '',
      defaultExpenseAccountName: '',
      categories: {},
    },
    services: {
      defaultIncomeAccountId: '',
      defaultIncomeAccountName: '',
      defaultExpenseAccountId: '',
      defaultExpenseAccountName: '',
      categories: {},
    },
  });
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  const [form, setForm] = useState({
    name: '',
    salesCode: '',
    costSales: '',
    incomeSales: '',
    incomeMarkup: '',
  });

  function normalizeText(value) {
    return String(value || '').trim().toLowerCase();
  }

  function accountById(id) {
    if (!id) return null;
    return qbAccounts.find((row) => String(row.id) === String(id)) || null;
  }

  function accountNameFromId(id) {
    const matched = accountById(id);
    return matched?.fullyQualifiedName || matched?.name || '';
  }

  function normalizeCategoryMappings(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    Object.entries(raw).forEach(([key, value]) => {
      if (!key) return;
      out[key] = {
        incomeAccountId: String(value?.incomeAccountId || '').trim(),
        incomeAccountName: String(value?.incomeAccountName || '').trim(),
        expenseAccountId: String(value?.expenseAccountId || '').trim(),
        expenseAccountName: String(value?.expenseAccountName || '').trim(),
      };
    });
    return out;
  }

  function toMappingGroup(raw) {
    return {
      defaultIncomeAccountId: String(raw?.defaultIncomeAccountId || '').trim(),
      defaultIncomeAccountName: String(raw?.defaultIncomeAccountName || '').trim(),
      defaultExpenseAccountId: String(raw?.defaultExpenseAccountId || '').trim(),
      defaultExpenseAccountName: String(raw?.defaultExpenseAccountName || '').trim(),
      categories: normalizeCategoryMappings(raw?.categories),
    };
  }

  function legacyMappingGroup(nextSalesAccounts) {
    const categoryRows = {};
    Object.entries(nextSalesAccounts || {}).forEach(([categoryName, row]) => {
      categoryRows[categoryName] = {
        incomeAccountId: '',
        incomeAccountName: String(row?.incomeSales || '').trim(),
        expenseAccountId: '',
        expenseAccountName: String(row?.costSales || '').trim(),
      };
    });
    return {
      defaultIncomeAccountId: '',
      defaultIncomeAccountName: '',
      defaultExpenseAccountId: '',
      defaultExpenseAccountName: '',
      categories: categoryRows,
    };
  }

  function hydrateMappingFromSettings(settings, nextSalesAccounts) {
    const raw = settings?.quickbooks?.accountMapping;
    if (raw && typeof raw === 'object') {
      return {
        products: toMappingGroup(raw.products),
        services: toMappingGroup(raw.services),
      };
    }

    const fallbackProducts = legacyMappingGroup(nextSalesAccounts);
    const fallbackServices = legacyMappingGroup(nextSalesAccounts);
    fallbackProducts.defaultIncomeAccountName = String(
      settings?.quickbooks?.defaultProductIncomeAccountName ||
      settings?.quickbooks?.productIncomeAccountName ||
      ''
    ).trim();
    fallbackProducts.defaultExpenseAccountName = String(
      settings?.quickbooks?.defaultProductExpenseAccountName ||
      settings?.quickbooks?.productExpenseAccountName ||
      ''
    ).trim();
    fallbackServices.defaultIncomeAccountName = String(
      settings?.quickbooks?.defaultServiceIncomeAccountName ||
      settings?.quickbooks?.serviceIncomeAccountName ||
      ''
    ).trim();
    fallbackServices.defaultExpenseAccountName = String(
      settings?.quickbooks?.defaultServiceExpenseAccountName ||
      settings?.quickbooks?.serviceExpenseAccountName ||
      ''
    ).trim();
    return {
      products: fallbackProducts,
      services: fallbackServices,
    };
  }

  function setDefaultMappingField(tabKey, field, nextValue) {
    setMapping((prev) => {
      const next = { ...prev, [tabKey]: { ...(prev[tabKey] || {}), categories: { ...(prev[tabKey]?.categories || {}) } } };
      next[tabKey][field] = nextValue;
      return next;
    });
  }

  function setCategoryMapping(tabKey, categoryName, patch) {
    setMapping((prev) => {
      const currentGroup = prev[tabKey] || { categories: {} };
      const currentCategories = currentGroup.categories || {};
      const currentRow = currentCategories[categoryName] || {
        incomeAccountId: '',
        incomeAccountName: '',
        expenseAccountId: '',
        expenseAccountName: '',
      };
      return {
        ...prev,
        [tabKey]: {
          ...currentGroup,
          categories: {
            ...currentCategories,
            [categoryName]: {
              ...currentRow,
              ...patch,
            },
          },
        },
      };
    });
  }

  function categoryListForTab(tabKey) {
    const set = new Set();
    categories.forEach((row) => {
      const name = String(row?.name || '').trim();
      if (!name) return;
      const type = String(row?.type || 'both').toLowerCase();
      if (type === 'both') {
        set.add(name);
        return;
      }
      if (tabKey === 'products' && type === 'product') set.add(name);
      if (tabKey === 'services' && type === 'service') set.add(name);
    });
    Object.keys(salesAccounts || {}).forEach((name) => {
      if (String(name || '').trim()) set.add(name);
    });
    Object.keys(mapping?.[tabKey]?.categories || {}).forEach((name) => {
      if (String(name || '').trim()) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  const incomeAccountOptions = qbAccounts
    .filter((account) => normalizeText(account.accountType).includes('income'))
    .sort((a, b) =>
      String(a.fullyQualifiedName || a.name).localeCompare(String(b.fullyQualifiedName || b.name))
    );
  const expenseAccountOptions = qbAccounts
    .filter((account) => {
      const type = normalizeText(account.accountType);
      return type.includes('cost of goods sold') || type.includes('expense');
    })
    .sort((a, b) =>
      String(a.fullyQualifiedName || a.name).localeCompare(String(b.fullyQualifiedName || b.name))
    );

  /* ======================================================
     LOCK BACKGROUND INTERACTION
  ====================================================== */
  useEffect(() => {
    document.body.classList.toggle('modal-open', showModal);
    return () => document.body.classList.remove('modal-open');
  }, [showModal]);

  /* ======================================================
     LOAD SETTINGS FROM AWS
  ====================================================== */
  useEffect(() => {
    async function load() {
      try {
        const [settingsRes, categoriesRes, accountsRes] = await Promise.all([
          fetch(`${API_BASE}/api/settings/${userId}`),
          fetch(`${API_BASE}/api/categories/${encodeURIComponent(userId)}`),
          token
            ? fetch(`${API_BASE}/api/quickbooks/accounts`, {
                headers: { Authorization: `Bearer ${token}` },
              })
            : Promise.resolve(null),
        ]);

        const settings = settingsRes.ok ? await settingsRes.json() : {};
        const nextSalesAccounts =
          settings?.salesAccounts && typeof settings.salesAccounts === 'object'
            ? settings.salesAccounts
            : {};
        setSalesAccounts(nextSalesAccounts);
        setMapping(hydrateMappingFromSettings(settings, nextSalesAccounts));
        setDefaultShippingDestination(settings?.defaultShippingDestination || 'designer_warehouse');
        setFreightDestinations(Array.isArray(settings?.freightDestinations) ? settings.freightDestinations : []);
        setCustomOrderStatuses(normalizeCustomStatuses(settings?.customOrderStatuses));

        if (categoriesRes.ok) {
          const categoryData = await categoriesRes.json().catch(() => []);
          setCategories(Array.isArray(categoryData) ? categoryData : []);
        } else {
          setCategories([]);
        }

        if (accountsRes?.ok) {
          const accountData = await accountsRes.json().catch(() => []);
          setQbAccounts(Array.isArray(accountData) ? accountData : []);
          setQbConnected(true);
        } else {
          setQbAccounts([]);
          setQbConnected(false);
        }
      } catch (err) {
        console.error('Failed to load sales/account mapping settings:', err);
        setSalesAccounts({});
        setCategories([]);
        setQbAccounts([]);
        setQbConnected(false);
      }
    }
    load();
  }, [API_BASE, userId, token]);

  /* ======================================================
     PERSIST TO AWS (AUTHORITATIVE)
  ====================================================== */
  const persistSalesAccounts = async (nextAccounts) => {
    const res = await fetch(`${API_BASE}/api/settings/${userId}/sales-accounts`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ salesAccounts: nextAccounts }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to save sales accounts');
    }
  };

  const persistQuickBooksAccountMapping = async (nextMapping) => {
    const res = await fetch(`${API_BASE}/api/settings/${userId}/quickbooks`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountMapping: nextMapping }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to save QuickBooks account mapping.');
    }
  };

  /* ======================================================
     ENSURE REQUIRED DEFAULTS (MERGE MISSING, KEEP EXISTING)
  ====================================================== */
  useEffect(() => {
    if (!salesAccounts) return;

    const next = { ...salesAccounts };
    const keyByNormalized = new Map(
      Object.keys(salesAccounts).map((key) => [normalizeText(key), key])
    );
    let changed = false;

    DEFAULT_ROWS.forEach(({ label, code, costSales, incomeSales, incomeMarkup }) => {
      const normalized = normalizeText(label);
      const matchedKey = keyByNormalized.get(normalized) || null;
      const targetKey = matchedKey || label;
      const current = next[targetKey] && typeof next[targetKey] === 'object' ? next[targetKey] : {};
      const merged = {
        salesCode: current.salesCode || code || '',
        costSales: current.costSales || costSales || '',
        incomeSales: current.incomeSales || incomeSales || '',
        incomeMarkup: current.incomeMarkup || incomeMarkup || incomeSales || '',
      };

      if (
        !matchedKey ||
        merged.salesCode !== current.salesCode ||
        merged.costSales !== current.costSales ||
        merged.incomeSales !== current.incomeSales ||
        merged.incomeMarkup !== current.incomeMarkup
      ) {
        next[targetKey] = merged;
        changed = true;
      }
    });

    if (!changed) return;

    setSalesAccounts(next);
    persistSalesAccounts(next).catch(console.error);
  }, [salesAccounts]);

  /* ======================================================
     MODAL HANDLERS
  ====================================================== */
  const openNew = () => {
    setEditingKey(null);
    setForm({
      name: '',
      salesCode: '',
      costSales: '',
      incomeSales: '',
      incomeMarkup: '',
    });
    setShowModal(true);
  };

  const openEdit = (key) => {
    const row = salesAccounts[key];
    setEditingKey(key);
    setForm({
      name: key,
      salesCode: row.salesCode,
      costSales: row.costSales,
      incomeSales: row.incomeSales,
      incomeMarkup: row.incomeMarkup,
    });
    setShowModal(true);
  };

  const saveModal = async () => {
    if (!form.name || !form.salesCode) return;

    const nextAccounts = { ...salesAccounts };
    if (editingKey) delete nextAccounts[editingKey];

    nextAccounts[form.name] = {
      salesCode: form.salesCode,
      costSales: form.costSales,
      incomeSales: form.incomeSales,
      incomeMarkup: form.incomeMarkup,
    };

    try {
      setSalesAccounts(nextAccounts);
      await persistSalesAccounts(nextAccounts);
      setShowModal(false);
    } catch (err) {
      console.error(err);
      notify('Failed to save sales account.');
    }
  };

  const saveQuickBooksMapping = async () => {
    if (!userId) return;
    setMappingSaving(true);
    try {
      await persistQuickBooksAccountMapping(mapping);
      notify('QuickBooks account mapping saved.');
    } catch (err) {
      console.error(err);
      notify('Failed to save QuickBooks account mapping.');
    } finally {
      setMappingSaving(false);
    }
  };

  const saveShippingDefaults = async (value) => {
    if (!userId) return;
    setShippingSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaultShippingDestination: value }),
      });
      if (!res.ok) throw new Error('Failed to save shipping default.');
      setDefaultShippingDestination(value);
      notify('Shipping default saved.');
    } catch (err) {
      console.error(err);
      notify('Failed to save shipping default.');
    } finally {
      setShippingSaving(false);
    }
  };

  const saveFreightDestinations = async (updated) => {
    if (!userId) return;
    setDestinationSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ freightDestinations: updated }),
      });
      if (!res.ok) throw new Error('Failed to save freight destination.');
      setFreightDestinations(updated);
    } catch (err) {
      console.error(err);
      notify('Failed to save freight destination.');
    } finally {
      setDestinationSaving(false);
    }
  };

  const addFreightDestination = async () => {
    const label = newDestinationLabel.trim();
    if (!label) return;
    const existingValues = [
      ...BUILT_IN_FREIGHT_DESTINATIONS.map((d) => d.value),
      ...freightDestinations.map((d) => d.value),
    ];
    const value = slugifyFreightDestination(label, existingValues);
    const updated = [...freightDestinations, { value, label }];
    await saveFreightDestinations(updated);
    setNewDestinationLabel('');
    notify('Freight destination added.');
  };

  const removeFreightDestination = async (value) => {
    const updated = freightDestinations.filter((d) => d.value !== value);
    await saveFreightDestinations(updated);
    if (defaultShippingDestination === value) {
      await saveShippingDefaults('designer_warehouse');
    }
    notify('Freight destination removed.');
  };

  // Saves the address for one destination. Built-ins have no stored entry until
  // an address is added, so this upserts by value rather than editing in place.
  const saveDestinationAddress = async () => {
    if (!editingDestination) return;
    const { value, label } = editingDestination;
    const address = getDestinationAddress(editingDestination);
    const existing = freightDestinations.find((d) => d.value === value);
    const updated = existing
      ? freightDestinations.map((d) => (d.value === value ? { ...d, ...address } : d))
      : [...freightDestinations, { value, label, ...address }];

    await saveFreightDestinations(updated);
    setEditingDestination(null);
    notify('Ship-to address saved.');
  };

  const freightDestinationOptions = getFreightDestinationOptions(freightDestinations);

  const saveCustomOrderStatuses = async (updated) => {
    if (!userId) return false;
    setStatusSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customOrderStatuses: updated }),
      });
      if (!res.ok) throw new Error('Failed to save order status.');
      setCustomOrderStatuses(updated);
      return true;
    } catch (err) {
      console.error(err);
      notify('Failed to save order status.');
      return false;
    } finally {
      setStatusSaving(false);
    }
  };

  const addCustomOrderStatus = async () => {
    const label = newStatusLabel.trim();
    if (!label) return;
    const taken = new Set(
      [...BUILT_IN_PO_STATUSES, ...BUILT_IN_SCHEDULE_STATUSES, ...customOrderStatuses].map((s) =>
        s.toLowerCase()
      )
    );
    if (taken.has(label.toLowerCase())) {
      notify('That status already exists.');
      return;
    }
    if (await saveCustomOrderStatuses([...customOrderStatuses, label])) {
      setNewStatusLabel('');
      notify('Order status added.');
    }
  };

  // Removing a status only takes it off the pickers — purchase orders and
  // schedule items already set to it keep showing it until they're changed.
  const removeCustomOrderStatus = async (label) => {
    if (await saveCustomOrderStatuses(customOrderStatuses.filter((s) => s !== label))) {
      notify('Order status removed.');
    }
  };

  if (!salesAccounts) return null;

  /* ======================================================
     RENDER
  ====================================================== */
  return (
    <div className="saleset-page">
      <section className="saleset-map-card">
        <div className="saleset-map-head">
          <h3>Shipping Defaults</h3>
          <p>Set the default freight destination for new line items on invoices, proposals, and purchase orders.</p>
        </div>
        <div className="saleset-map-default-grid">
          <div className="saleset-map-field">
            <label>Default Freight Destination</label>
            <select
              value={defaultShippingDestination}
              onChange={(e) => saveShippingDefaults(e.target.value)}
              disabled={shippingSaving}
            >
              {freightDestinationOptions.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="saleset-map-field" style={{ marginTop: 16 }}>
          <label>Freight Destinations</label>
          <p className="saleset-empty-hint" style={{ marginBottom: 8 }}>
            Save the receiver or workroom address on a destination and picking it on a purchase
            order fills Ship To automatically. Will Call never carries an address — the vendor
            holds the goods for pickup.
          </p>
          <div className="saleset-destination-rows">
            {freightDestinationOptions.map((d) => {
              const isCustom = !BUILT_IN_FREIGHT_DESTINATIONS.some((b) => b.value === d.value);
              const isWillCall = d.value === WILL_CALL_DESTINATION;
              const address = getDestinationAddress(d);
              const summary = [
                address.company,
                address.street,
                [address.city, address.state, address.zip].filter(Boolean).join(', '),
              ]
                .filter(Boolean)
                .join(' · ');

              return (
                <div className="saleset-destination-row" key={d.value}>
                  <div className="saleset-destination-row-main">
                    <span className="saleset-destination-row-label">{d.label}</span>
                    <span className="saleset-destination-row-address">
                      {isWillCall
                        ? 'Picked up at the vendor — no ship-to address'
                        : summary || 'No saved address'}
                    </span>
                  </div>
                  <div className="saleset-destination-row-actions">
                    {!isWillCall && (
                      <button
                        type="button"
                        className="saleset-destination-edit"
                        onClick={() => setEditingDestination({ ...address, value: d.value, label: d.label })}
                        disabled={destinationSaving}
                        aria-label={`Edit address for ${d.label}`}
                      >
                        <Pencil size={12} /> {hasDestinationAddress(d) ? 'Edit address' : 'Add address'}
                      </button>
                    )}
                    {isCustom && (
                      <button
                        type="button"
                        className="saleset-destination-remove"
                        onClick={() => removeFreightDestination(d.value)}
                        disabled={destinationSaving}
                        aria-label={`Remove ${d.label}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="saleset-destination-add">
            <input
              type="text"
              placeholder="e.g. Direct to Job Site"
              value={newDestinationLabel}
              onChange={(e) => setNewDestinationLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFreightDestination();
                }
              }}
              disabled={destinationSaving}
            />
            <button
              type="button"
              className="saleset-primary-btn"
              onClick={addFreightDestination}
              disabled={destinationSaving || !newDestinationLabel.trim()}
            >
              <Plus size={14} /> Add
            </button>
          </div>
        </div>
      </section>

      <section className="saleset-map-card">
        <div className="saleset-map-head">
          <h3>Order Statuses</h3>
          <p>
            Add your own statuses to the built-in ones. Custom statuses appear on purchase orders
            (order and item level), on schedule items, and as filters on the Procurement Tracker.
            You can also add one on the fly from a purchase order&apos;s status dropdown — this is
            where they&apos;re all listed and removed.
          </p>
        </div>

        <div className="saleset-map-field">
          <label>Purchase Order Statuses</label>
          <div className="saleset-destination-list">
            {BUILT_IN_PO_STATUSES.map((s) => (
              <span className="saleset-destination-chip" key={`po-${s}`}>
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="saleset-map-field" style={{ marginTop: 16 }}>
          <label>Schedule Statuses</label>
          <div className="saleset-destination-list">
            {BUILT_IN_SCHEDULE_STATUSES.map((s) => (
              <span className="saleset-destination-chip" key={`sched-${s}`}>
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="saleset-map-field" style={{ marginTop: 16 }}>
          <label>Custom Statuses</label>
          <div className="saleset-destination-list">
            {customOrderStatuses.length === 0 ? (
              <span className="saleset-empty-hint">No custom statuses yet.</span>
            ) : (
              customOrderStatuses.map((s) => (
                <span className="saleset-destination-chip" key={s}>
                  {s}
                  <button
                    type="button"
                    className="saleset-destination-remove"
                    onClick={() => removeCustomOrderStatus(s)}
                    disabled={statusSaving}
                    aria-label={`Remove ${s}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="saleset-destination-add">
            <input
              type="text"
              placeholder="e.g. At Workroom"
              value={newStatusLabel}
              onChange={(e) => setNewStatusLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomOrderStatus();
                }
              }}
              disabled={statusSaving}
            />
            <button
              type="button"
              className="saleset-primary-btn"
              onClick={addCustomOrderStatus}
              disabled={statusSaving || !newStatusLabel.trim()}
            >
              <Plus size={14} /> Add
            </button>
          </div>
        </div>
      </section>

      <section className="saleset-map-card">
        <div className="saleset-map-head">
          <h3>QuickBooks Account Mapping</h3>
          <p>
            Directly map categories to QuickBooks income and expense accounts by account ID.
          </p>
        </div>

        {!qbConnected && (
          <div className="saleset-map-warning">
            QuickBooks account list is unavailable. Connect QuickBooks to select direct mappings.
          </div>
        )}

        <div className="saleset-map-tabs">
          <button
            type="button"
            className={mappingTab === 'products' ? 'saleset-tab-btn active' : 'saleset-tab-btn'}
            onClick={() => setMappingTab('products')}
          >
            Products
          </button>
          <button
            type="button"
            className={mappingTab === 'services' ? 'saleset-tab-btn active' : 'saleset-tab-btn'}
            onClick={() => setMappingTab('services')}
          >
            Services
          </button>
        </div>

        <div className="saleset-map-block">
          <h4>Default Mapping</h4>
          <div className="saleset-map-default-grid">
            <div className="saleset-map-field">
              <label>Default Income</label>
              <select
                value={mapping?.[mappingTab]?.defaultIncomeAccountId || ''}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setDefaultMappingField(mappingTab, 'defaultIncomeAccountId', nextId);
                  setDefaultMappingField(
                    mappingTab,
                    'defaultIncomeAccountName',
                    nextId ? accountNameFromId(nextId) : ''
                  );
                }}
              >
                <option value="">Select income account</option>
                {incomeAccountOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.fullyQualifiedName || account.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="saleset-map-field">
              <label>Default Expense</label>
              <select
                value={mapping?.[mappingTab]?.defaultExpenseAccountId || ''}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setDefaultMappingField(mappingTab, 'defaultExpenseAccountId', nextId);
                  setDefaultMappingField(
                    mappingTab,
                    'defaultExpenseAccountName',
                    nextId ? accountNameFromId(nextId) : ''
                  );
                }}
              >
                <option value="">Select expense account</option>
                {expenseAccountOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.fullyQualifiedName || account.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="saleset-map-block">
          <h4>Mapping by Category</h4>
          <div className="saleset-map-categories">
            <div className="saleset-map-row saleset-map-row-head">
              <span>Category</span>
              <span>Income Account</span>
              <span>Expense Account</span>
            </div>
            {categoryListForTab(mappingTab).map((categoryName) => {
              const row = mapping?.[mappingTab]?.categories?.[categoryName] || {
                incomeAccountId: '',
                expenseAccountId: '',
              };
              return (
                <div key={`${mappingTab}-${categoryName}`} className="saleset-map-row">
                  <div className="saleset-map-category">{categoryName}</div>
                  <select
                    value={row.incomeAccountId || ''}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setCategoryMapping(mappingTab, categoryName, {
                        incomeAccountId: nextId,
                        incomeAccountName: nextId ? accountNameFromId(nextId) : '',
                      });
                    }}
                  >
                    <option value="">Default income</option>
                    {incomeAccountOptions.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.fullyQualifiedName || account.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={row.expenseAccountId || ''}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setCategoryMapping(mappingTab, categoryName, {
                        expenseAccountId: nextId,
                        expenseAccountName: nextId ? accountNameFromId(nextId) : '',
                      });
                    }}
                  >
                    <option value="">Default expense</option>
                    {expenseAccountOptions.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.fullyQualifiedName || account.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        <div className="saleset-map-actions">
          <button
            type="button"
            className="saleset-primary-btn"
            onClick={saveQuickBooksMapping}
            disabled={mappingSaving}
          >
            {mappingSaving ? 'Saving...' : 'Save QuickBooks Mapping'}
          </button>
        </div>
      </section>

      <div className="saleset-legacy-toggle">
        <div>
          <h3>Legacy Sales Accounts</h3>
          <p>
            Optional legacy mappings used as compatibility fallback and sales-code labels.
          </p>
        </div>
        <div className="saleset-actions">
          <button
            type="button"
            className="saleset-ghost-btn"
            onClick={() => setShowLegacySection((prev) => !prev)}
          >
            {showLegacySection ? 'Hide Legacy Section' : 'Show Legacy Section'}
          </button>
        </div>
      </div>

      {showLegacySection && (
        <>
          <div className="saleset-header">
            <h2>Sales Account</h2>

            <div className="saleset-actions">
              <button className="saleset-primary-btn" onClick={openNew}>
                <Plus size={14} /> Add New
              </button>
            </div>
          </div>

          <table className="saleset-table">
            <thead>
              <tr>
                <th>Sales Account</th>
                <th>Cost Sales Account</th>
                <th>Income Sales Account</th>
                <th>Income Mark Up Account</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {Object.entries(salesAccounts).map(([key, row]) => (
                <tr key={key}>
                  <td className="category">{key}</td>
                  <td>{row.costSales}</td>
                  <td>{row.incomeSales}</td>
                  <td>{row.incomeMarkup}</td>
                  <td className="saleset-action">
                    <Pencil size={14} onClick={() => openEdit(key)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ======================================================
          MODAL
      ====================================================== */}
      {showModal &&
        createPortal(
          <div className="saleset-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="saleset-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{editingKey ? 'Edit Sales Account' : 'New Sales Account'}</h3>

              <form className="saleset-form">
                <label>Sales Account</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />

                <label>Sales Code</label>
                <input
                  value={form.salesCode}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      salesCode: e.target.value.toUpperCase(),
                    })
                  }
                />

                <label>Cost Sales Account</label>
                <input
                  value={form.costSales}
                  onChange={(e) => setForm({ ...form, costSales: e.target.value })}
                />

                <label>Income Sales Account</label>
                <input
                  value={form.incomeSales}
                  onChange={(e) => setForm({ ...form, incomeSales: e.target.value })}
                />

                <label>Income Mark Up Account</label>
                <input
                  value={form.incomeMarkup}
                  onChange={(e) => setForm({ ...form, incomeMarkup: e.target.value })}
                />

                <div className="saleset-modal-actions">
                  <button
                    type="button"
                    className="saleset-cancel-btn"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="button" className="saleset-save-btn" onClick={saveModal}>
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {editingDestination &&
        createPortal(
          <div className="saleset-modal-overlay" onClick={() => setEditingDestination(null)}>
            <div className="saleset-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{editingDestination.label} — Ship-To Address</h3>

              <form className="saleset-form">
                <label>Company / Receiver Name</label>
                <input
                  autoFocus
                  value={editingDestination.company}
                  placeholder="e.g. Sunbelt Receiving Warehouse"
                  onChange={(e) =>
                    setEditingDestination((d) => ({ ...d, company: e.target.value }))
                  }
                />

                <label>Street</label>
                <AddressAutocomplete
                  value={editingDestination.street}
                  onChange={(e) => setEditingDestination((d) => ({ ...d, street: e.target.value }))}
                  onSelect={({ street, city, state, zip }) =>
                    setEditingDestination((d) => ({ ...d, street, city, state, zip }))
                  }
                />

                <label>City</label>
                <input
                  value={editingDestination.city}
                  onChange={(e) => setEditingDestination((d) => ({ ...d, city: e.target.value }))}
                />

                <label>State</label>
                <input
                  value={editingDestination.state}
                  onChange={(e) => setEditingDestination((d) => ({ ...d, state: e.target.value }))}
                />

                <label>Zip</label>
                <input
                  value={editingDestination.zip}
                  onChange={(e) => setEditingDestination((d) => ({ ...d, zip: e.target.value }))}
                />

                <label>Phone</label>
                <input
                  value={editingDestination.phone}
                  onChange={(e) => setEditingDestination((d) => ({ ...d, phone: e.target.value }))}
                />

                <label>Email</label>
                <input
                  type="email"
                  value={editingDestination.email}
                  onChange={(e) => setEditingDestination((d) => ({ ...d, email: e.target.value }))}
                />

                <div className="saleset-modal-actions">
                  <button
                    type="button"
                    className="saleset-cancel-btn"
                    onClick={() => setEditingDestination(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="saleset-save-btn"
                    onClick={saveDestinationAddress}
                    disabled={destinationSaving}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
